import { ReviewResponse } from '../types';
import { legacyNegativeId, safeNegativeId } from '../utils/idUtils';
import { offlineDatabase, OfflineReview, OfflineResponse } from './offlineDatabase';
import {
  buildResponseSyncGroups,
  resolveResponseItemId,
  ResponseSyncGroup,
} from './offlineResponseSyncPlanner';
import { selectBestResponseForItem } from './reviewResponseSelector';

type IdMappingRecord = {
  tempId: string;
  realId: number;
  entityType: string;
};

export interface OfflineResponseSyncDatabase {
  executeSql: typeof offlineDatabase.executeSql;
  getAllIdMappings: typeof offlineDatabase.getAllIdMappings;
  getPhotosFromQueue: typeof offlineDatabase.getPhotosFromQueue;
  getResponse?: typeof offlineDatabase.getResponse;
  getResponsesByReviewId: typeof offlineDatabase.getResponsesByReviewId;
  getResponsesBySyncStatus: typeof offlineDatabase.getResponsesBySyncStatus;
  getReviews: typeof offlineDatabase.getReviews;
  getPendingOfflineOnDemandItems?: typeof offlineDatabase.getPendingOfflineOnDemandItems;
  getPendingOfflineRooms?: typeof offlineDatabase.getPendingOfflineRooms;
  getPendingPhotoNotes?: typeof offlineDatabase.getPendingPhotoNotes;
  saveIdMapping: typeof offlineDatabase.saveIdMapping;
  updateResponse: typeof offlineDatabase.updateResponse;
  updateResponseIfUnchanged?: typeof offlineDatabase.updateResponseIfUnchanged;
  updateReview: typeof offlineDatabase.updateReview;
  markOfflineMutationsSyncedForResponse?: typeof offlineDatabase.markOfflineMutationsSyncedForResponse;
  recordOfflineMutation?: typeof offlineDatabase.recordOfflineMutation;
}

export interface OfflineResponseSyncReviewApi {
  updateResponse(
    reviewId: number,
    itemId: number,
    data: Partial<ReviewResponse>,
    options?: { forceOnline?: boolean }
  ): Promise<ReviewResponse>;
  getReviewDetails?(reviewId: number): Promise<{ responses?: ReviewResponse[] }>;
}

interface OfflineResponseSyncServiceOptions {
  database: OfflineResponseSyncDatabase;
  reviewApi: OfflineResponseSyncReviewApi;
  getRealId(tempId: string): Promise<number | undefined>;
  mapTempIdToRealId(tempId: string, realId: number): Promise<void>;
  processPhotoQueue?(): Promise<void>;
}

export class OfflineResponseSyncService {
  constructor(private readonly options: OfflineResponseSyncServiceOptions) {}

  async syncSingleResponse(
    realReviewId: number,
    offlineResponse: OfflineResponse,
    forcedItemId?: number
  ): Promise<boolean> {
    const { database, reviewApi } = this.options;

    try {
      const roomMappings = await this.getRoomMappings();
      const realRoomId = this.resolveRoomIdFromMappings(offlineResponse.roomId, roomMappings);
      if (realRoomId === undefined && offlineResponse.roomId && offlineResponse.roomId < 0) {
        return false;
      }

      const resolvedResponseData = this.resolveResponseDataRoomIds(
        offlineResponse.responseData,
        roomMappings
      );
      if (resolvedResponseData.hasUnresolvedRoomIds) {
        return false;
      }

      const responseData: Partial<ReviewResponse> = {
        isPresent: offlineResponse.isPresent,
        status: offlineResponse.status,
        responseData: resolvedResponseData.responseData,
        notes: offlineResponse.notes,
        findings: offlineResponse.findings || [],
        roomId: realRoomId,
        floor: offlineResponse.floor,
      };

      const realItemId = forcedItemId ?? await this.resolveResponseItemId(offlineResponse);
      if (realItemId == null) {
        return false;
      }

      const serverResponse = await this.getServerResponse(realReviewId, realItemId);
      if (serverResponse && this.shouldKeepServerResponse(offlineResponse, serverResponse)) {
        if (offlineResponse.tempId) {
          await database.saveIdMapping(offlineResponse.tempId, serverResponse.id, 'response');
          await this.options.mapTempIdToRealId(offlineResponse.tempId, serverResponse.id);
        }

        const didApplyServerWinner = await this.applyResponseSyncResultIfUnchanged(
          offlineResponse,
          this.buildServerWinnerUpdates(offlineResponse, serverResponse)
        );
        if (!didApplyServerWinner) {
          console.warn('[OfflineResponseSyncService] Ignored stale server-winner result because local response changed during sync:', {
            reviewId: realReviewId,
            itemId: realItemId,
            localResponseId: offlineResponse.id,
            localRevision: offlineResponse.localRevision,
            winner: 'local',
          });
          this.schedulePhotoQueueRetry();
          return false;
        }
        await database.recordOfflineMutation?.({
          entityType: 'response',
          entityId: offlineResponse.id,
          reviewId: realReviewId,
          itemId: realItemId,
          mutationType: 'sync_skipped_server_winner',
          payload: {
            serverResponseId: serverResponse.id,
            serverUpdatedAt: (serverResponse as any).updatedAt,
            serverStatus: serverResponse.status,
            localUpdatedAt: offlineResponse.updatedAt,
            localMutationAt: offlineResponse.lastLocalMutationAt,
            localStatus: offlineResponse.status,
          },
          previousSnapshot: offlineResponse,
          status: 'synced',
        });
        this.schedulePhotoQueueRetry();
        console.warn('[OfflineResponseSyncService] Skipped response upload because server copy wins:', {
          reviewId: realReviewId,
          itemId: realItemId,
          localResponseId: offlineResponse.id,
          serverResponseId: serverResponse.id,
          localUpdatedAt: offlineResponse.updatedAt,
          localMutationAt: offlineResponse.lastLocalMutationAt,
          serverUpdatedAt: (serverResponse as any).updatedAt,
          localStatus: offlineResponse.status,
          serverStatus: serverResponse.status,
          winner: 'server',
        });
        return true;
      }

      const syncedResponse = await reviewApi.updateResponse(
        realReviewId,
        realItemId,
        responseData,
        { forceOnline: true }
      );

      if (offlineResponse.tempId) {
        await database.saveIdMapping(offlineResponse.tempId, syncedResponse.id, 'response');
        await this.options.mapTempIdToRealId(offlineResponse.tempId, syncedResponse.id);
        this.schedulePhotoQueueRetry();
      }

      const didApplyLocalSync = await this.applyResponseSyncResultIfUnchanged(offlineResponse, {
        ...(realRoomId !== offlineResponse.roomId ? { roomId: realRoomId } : {}),
        ...(resolvedResponseData.didChange ? { responseData: resolvedResponseData.responseData } : {}),
        findings: syncedResponse.findings || offlineResponse.findings || [],
        syncStatus: 'synced',
        syncError: null,
        lastServerSeenAt: (syncedResponse as any).updatedAt || new Date(),
        serverRevision: (offlineResponse.serverRevision ?? 0) + 1,
        conflictStatus: 'none',
      });
      if (!didApplyLocalSync) {
        console.warn('[OfflineResponseSyncService] Kept response pending because local response changed during upload:', {
          reviewId: realReviewId,
          itemId: realItemId,
          localResponseId: offlineResponse.id,
          localRevision: offlineResponse.localRevision,
          serverResponseId: syncedResponse.id,
        });
        return false;
      }
      await database.markOfflineMutationsSyncedForResponse?.(realReviewId, realItemId);

      return true;
    } catch (error) {
      await database.updateResponse(offlineResponse.id, {
        syncStatus: 'failed',
        syncError: error instanceof Error ? error.message : 'Unknown sync error',
      });
      throw error;
    }
  }

  async syncPendingResponses(): Promise<void> {
    const { database } = this.options;

    try {
      const allReviews = await database.getReviews({});
      const responsesProcessed = new Set<number>();

      for (const review of allReviews) {
        try {
          const responses = await database.getResponsesByReviewId(review.id);
          const responsesToSync = responses.filter(
            response => response.syncStatus === 'pending' || response.syncStatus === 'failed'
          );

          if (responsesToSync.length === 0) {
            continue;
          }

          const realReviewId = await this.resolveReviewIdForSync(review);
          if (realReviewId == null) {
            continue;
          }

          await this.resetFailedResponses(responsesToSync);
          const responseGroups = await this.groupResponsesForSync(realReviewId, responsesToSync);

          for (const group of responseGroups) {
            try {
              const didSync = await this.syncSingleResponse(realReviewId, group.response);
              if (!didSync) {
                continue;
              }

              responsesProcessed.add(group.response.id);
              await this.markDuplicateResponsesSynced(group.duplicates, group.response);
              for (const duplicate of group.duplicates) {
                responsesProcessed.add(duplicate.id);
              }
            } catch (error) {
              console.error('[OfflineResponseSyncService] Response sync failed:', group.response.id, error);
            }
          }
        } catch (error) {
          console.error('[OfflineResponseSyncService] Error syncing responses for review:', review.id, error);
        }
      }

      await this.syncOrphanResponses(responsesProcessed);
      await this.markFullySyncedReviews();
    } catch (error) {
      console.error('[OfflineResponseSyncService] Error in syncPendingResponses:', error);
      throw error;
    }
  }

  private async getRoomMappings(): Promise<IdMappingRecord[]> {
    const mappings = await this.options.database.getAllIdMappings();
    return mappings.filter(mapping => mapping.entityType === 'room');
  }

  private resolveRoomIdFromMappings(
    roomId: number | undefined,
    roomMappings: IdMappingRecord[]
  ): number | undefined {
    if (!roomId || roomId >= 0) {
      return roomId;
    }

    const roomMapping = roomMappings.find(mapping => {
      return safeNegativeId(mapping.tempId) === roomId || legacyNegativeId(mapping.tempId) === roomId;
    });

    return roomMapping?.realId;
  }

  private resolveResponseDataRoomIds(
    responseData: ReviewResponse['responseData'],
    roomMappings: IdMappingRecord[]
  ): {
    responseData: ReviewResponse['responseData'];
    didChange: boolean;
    hasUnresolvedRoomIds: boolean;
  } {
    if (!responseData || typeof responseData !== 'object' || Array.isArray(responseData)) {
      return {
        responseData,
        didChange: false,
        hasUnresolvedRoomIds: false,
      };
    }

    let didChange = false;
    let hasUnresolvedRoomIds = false;
    const nextResponseData: Record<string, unknown> = { ...responseData };

    for (const [key, value] of Object.entries(responseData)) {
      if (!/^roomId_item_\d+$/.test(key)) {
        continue;
      }

      const roomId = typeof value === 'number'
        ? value
        : typeof value === 'string'
          ? Number(value)
          : NaN;

      if (!Number.isFinite(roomId) || roomId >= 0) {
        continue;
      }

      const realRoomId = this.resolveRoomIdFromMappings(roomId, roomMappings);
      if (realRoomId === undefined) {
        hasUnresolvedRoomIds = true;
        continue;
      }

      nextResponseData[key] = realRoomId;
      didChange = true;
    }

    return {
      responseData: didChange ? nextResponseData as ReviewResponse['responseData'] : responseData,
      didChange,
      hasUnresolvedRoomIds,
    };
  }

  private async resolveResponseItemId(response: OfflineResponse): Promise<number | null> {
    const { database } = this.options;

    return resolveResponseItemId(response, {
      executeSql: (sql, params) => database.executeSql(sql, params),
      getAllIdMappings: () => database.getAllIdMappings(),
    });
  }

  private async getServerResponse(
    reviewId: number,
    itemId: number
  ): Promise<ReviewResponse | null> {
    if (!this.options.reviewApi.getReviewDetails) {
      return null;
    }

    try {
      const serverReview = await this.options.reviewApi.getReviewDetails(reviewId);
      return selectBestResponseForItem(serverReview.responses, itemId) || null;
    } catch (error) {
      console.warn('[OfflineResponseSyncService] Failed to fetch server response before sync:', {
        reviewId,
        itemId,
        error,
      });
      return null;
    }
  }

  private shouldKeepServerResponse(
    offlineResponse: OfflineResponse,
    serverResponse: ReviewResponse
  ): boolean {
    if (serverResponse.status === 'completed' && offlineResponse.status !== 'completed') {
      return true;
    }

    const localPriority = this.statusPriority(offlineResponse.status);
    const serverPriority = this.statusPriority(serverResponse.status);
    if (
      localPriority > serverPriority &&
      this.isEmptyServerPlaceholder(serverResponse) &&
      this.hasMeaningfulLocalResponse(offlineResponse)
    ) {
      return false;
    }

    const localTimestamp = this.timestampMs(offlineResponse.lastLocalMutationAt || offlineResponse.updatedAt);
    const serverTimestamp = this.timestampMs((serverResponse as any).updatedAt);
    if (localTimestamp != null && serverTimestamp != null) {
      if (serverTimestamp > localTimestamp) {
        return true;
      }

      if (localTimestamp > serverTimestamp) {
        return false;
      }
    }

    return serverPriority > localPriority;
  }

  private isEmptyServerPlaceholder(serverResponse: ReviewResponse): boolean {
    return this.statusPriority(serverResponse.status) === 0 &&
      !this.hasText((serverResponse as any).notes) &&
      !this.hasResponseData(serverResponse.responseData) &&
      ((serverResponse as any).roomId === null || (serverResponse as any).roomId === undefined) &&
      !this.hasText((serverResponse as any).floor) &&
      (!Array.isArray(serverResponse.findings) || serverResponse.findings.length === 0) &&
      (!Array.isArray((serverResponse as any).photos) || (serverResponse as any).photos.length === 0);
  }

  private hasMeaningfulLocalResponse(response: OfflineResponse): boolean {
    return this.statusPriority(response.status) > 0 ||
      this.hasText(response.notes) ||
      this.hasResponseData(response.responseData) ||
      Boolean(response.findings?.length) ||
      response.roomId !== null && response.roomId !== undefined ||
      this.hasText(response.floor);
  }

  private hasText(value: unknown): boolean {
    return typeof value === 'string' && value.trim().length > 0;
  }

  private hasResponseData(value: unknown): boolean {
    if (!value || typeof value !== 'object') {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return Object.keys(value as Record<string, unknown>).length > 0;
  }

  private statusPriority(status: unknown): number {
    if (status === 'completed') return 2;
    if (status === 'in_progress') return 1;
    return 0;
  }

  private timestampMs(value: unknown): number | null {
    if (!value) {
      return null;
    }

    const time = new Date(value as any).getTime();
    return Number.isFinite(time) ? time : null;
  }

  private buildServerWinnerUpdates(
    offlineResponse: OfflineResponse,
    serverResponse: ReviewResponse
  ): Partial<OfflineResponse> {
    return {
      isPresent: serverResponse.isPresent,
      status: serverResponse.status,
      responseData: serverResponse.responseData,
      notes: (serverResponse as any).notes ?? null,
      findings: serverResponse.findings || [],
      roomId: (serverResponse as any).roomId ?? null,
      floor: (serverResponse as any).floor ?? null,
      syncStatus: 'synced',
      syncError: null,
      lastServerSeenAt: (serverResponse as any).updatedAt || new Date(),
      serverRevision: (offlineResponse.serverRevision ?? 0) + 1,
      conflictStatus: 'server_wins',
    };
  }

  private async applyResponseSyncResultIfUnchanged(
    baseline: OfflineResponse,
    updates: Partial<OfflineResponse>
  ): Promise<boolean> {
    const expectedRevision = baseline.localRevision ?? 0;

    if (this.options.database.updateResponseIfUnchanged) {
      return this.options.database.updateResponseIfUnchanged(
        baseline.id,
        expectedRevision,
        updates
      );
    }

    const latest = await this.getLatestResponse(baseline);
    if (latest && this.hasLocalMutationAdvanced(baseline, latest)) {
      return false;
    }

    await this.options.database.updateResponse(baseline.id, updates);
    return true;
  }

  private async getLatestResponse(response: OfflineResponse): Promise<OfflineResponse | null> {
    if (this.options.database.getResponse) {
      return this.options.database.getResponse(response.id);
    }

    const responses = await this.options.database.getResponsesByReviewId(response.reviewId);
    return responses.find(candidate => candidate.id === response.id) || null;
  }

  private hasLocalMutationAdvanced(
    baseline: OfflineResponse,
    latest: OfflineResponse
  ): boolean {
    const baselineRevision = baseline.localRevision ?? 0;
    const latestRevision = latest.localRevision ?? 0;
    if (latestRevision > baselineRevision) {
      return true;
    }

    const baselineMutationAt = this.timestampMs(baseline.lastLocalMutationAt || baseline.updatedAt);
    const latestMutationAt = this.timestampMs(latest.lastLocalMutationAt || latest.updatedAt);
    return baselineMutationAt != null &&
      latestMutationAt != null &&
      latestMutationAt > baselineMutationAt;
  }

  private async resolveReviewIdForSync(review: OfflineReview): Promise<number | null> {
    if (!review.tempId) {
      return review.id;
    }

    const mappedId = await this.options.getRealId(review.tempId);
    if (mappedId) {
      return mappedId;
    }

    console.error('[OfflineResponseSyncService] Review has tempId but no mapping yet:', review.tempId);
    return null;
  }

  private async resetFailedResponses(responses: OfflineResponse[]): Promise<void> {
    for (const response of responses) {
      if (response.syncStatus !== 'failed') {
        continue;
      }

      try {
        await this.options.database.updateResponse(response.id, {
          syncStatus: 'pending',
          syncError: undefined,
        });
        response.syncStatus = 'pending';
        response.syncError = undefined;
      } catch (error) {
        console.error('[OfflineResponseSyncService] Failed to reset response before sync:', response.id, error);
      }
    }
  }

  private async groupResponsesForSync(
    realReviewId: number,
    responses: OfflineResponse[]
  ): Promise<ResponseSyncGroup[]> {
    const candidates = await Promise.all(
      responses.map(async response => ({
        response,
        realItemId: await this.resolveResponseItemId(response),
      }))
    );

    return buildResponseSyncGroups(realReviewId, candidates);
  }

  private async markDuplicateResponsesSynced(
    duplicates: OfflineResponse[],
    canonicalResponse: OfflineResponse
  ): Promise<void> {
    for (const duplicate of duplicates) {
      const didMarkDuplicate = await this.applyResponseSyncResultIfUnchanged(duplicate, {
        isPresent: canonicalResponse.isPresent,
        status: canonicalResponse.status,
        responseData: canonicalResponse.responseData,
        notes: canonicalResponse.notes,
        findings: canonicalResponse.findings || [],
        roomId: canonicalResponse.roomId,
        floor: canonicalResponse.floor,
        syncStatus: 'synced',
        syncError: null,
        conflictStatus: 'none',
      });
      if (!didMarkDuplicate) {
        console.warn('[OfflineResponseSyncService] Kept duplicate response pending because it changed during sync:', {
          responseId: duplicate.id,
          itemId: duplicate.itemId,
          localRevision: duplicate.localRevision,
        });
      }
    }
  }

  private async syncOrphanResponses(responsesProcessed: Set<number>): Promise<void> {
    const pendingResponses = await this.options.database.getResponsesBySyncStatus(['pending', 'failed']);

    for (const response of pendingResponses) {
      if (responsesProcessed.has(response.id) || !response.reviewId || response.reviewId < 0) {
        continue;
      }

      try {
        if (response.syncStatus === 'failed') {
          await this.options.database.updateResponse(response.id, {
            syncStatus: 'pending',
            syncError: undefined,
          });
        }

        const didSync = await this.syncSingleResponse(response.reviewId, response);
        if (didSync) {
          responsesProcessed.add(response.id);
        }
      } catch (error) {
        console.error('[OfflineResponseSyncService] Orphan response sync failed:', response.id, error);
      }
    }
  }

  private async markFullySyncedReviews(): Promise<void> {
    try {
      const allReviews = await this.options.database.getReviews({});
      const unresolvedRooms = this.options.database.getPendingOfflineRooms
        ? await this.options.database.getPendingOfflineRooms()
        : [];
      const unresolvedItems = this.options.database.getPendingOfflineOnDemandItems
        ? await this.options.database.getPendingOfflineOnDemandItems()
        : [];
      const pendingPhotoNotes = this.options.database.getPendingPhotoNotes
        ? await this.options.database.getPendingPhotoNotes()
        : [];
      const queuedPhotos = await this.options.database.getPhotosFromQueue({});

      for (const review of allReviews) {
        // A review-level write failure must remain retryable. Child response
        // cleanup cannot prove that contacts, notes, or projects reached the
        // server, so it must not erase the record-sync failure.
        if (review.syncStatus === 'synced' || review.syncStatus === 'failed') {
          continue;
        }

        const hasPendingLifecycleAction = Boolean(
          review.metadata &&
          typeof review.metadata === 'object' &&
          (review.metadata as Record<string, unknown>).pendingLifecycleAction
        );
        const hasPendingRooms = unresolvedRooms.some(room =>
          Number(room.property_id) === Number(review.propertyId) &&
          room.sync_status !== 'synced'
        );
        const hasPendingItems = unresolvedItems.some(item =>
          Number(item.review_id) === Number(review.id) &&
          item.sync_status !== 'synced'
        );

        const responses = await this.options.database.getResponsesByReviewId(review.id);
        const responseIds = new Set(responses.map(response => Number(response.id)));
        const hasPendingPhotos = queuedPhotos.some(photo =>
          (photo.status === 'pending' || photo.status === 'uploading' || photo.status === 'failed') &&
          (
            Number(photo.metadata?.reviewId) === Number(review.id) ||
            Number(photo.metadata?.localReviewId) === Number(review.id) ||
            responseIds.has(Number(photo.reviewResponseId))
          )
        );
        const hasPendingPhotoNotes = pendingPhotoNotes.some(note =>
          note.responseId != null && responseIds.has(Number(note.responseId))
        );

        if (
          !hasPendingLifecycleAction &&
          !hasPendingRooms &&
          !hasPendingItems &&
          !hasPendingPhotos &&
          !hasPendingPhotoNotes &&
          (responses.length === 0 || responses.every(response => response.syncStatus === 'synced'))
        ) {
          await this.options.database.updateReview(review.id, {
            syncStatus: 'synced',
            syncError: undefined,
            lastServerSeenAt: new Date(),
            conflictStatus: 'none',
          });
        }
      }
    } catch (error) {
      console.error('[OfflineResponseSyncService] Error marking fully synced reviews:', error);
    }
  }

  private schedulePhotoQueueRetry(): void {
    if (!this.options.processPhotoQueue) {
      return;
    }

    setTimeout(() => {
      this.options.processPhotoQueue?.().catch((error: Error) => {
        console.error('[OfflineResponseSyncService] Photo queue processing failed:', error);
      });
    }, 100);
  }
}
